import type { StockTakeListResponse, StockTakeRound } from '@/infrastructure/FulfillmentAdminClient';

/** How many rounds one page of the open list asks for, the most the server serves at once. */
export const OPEN_ROUNDS_PAGE_SIZE = 200;

/** The most pages read for open rounds: far past a warehouse of shelves, short of a loop on a cursor that never ends. */
export const OPEN_ROUNDS_MAX_PAGES = 50;

/**
 * Every open round, page by page, so a walk over more shelves than one page is picked back up whole. The first page
 * alone left the shelves past it out of a refresh (a walk of 214 shelves read back 100 of them).
 */
export const listEveryOpenRound = async (
  list: (params: { status: 'OPEN'; limit: number; before?: string }) => Promise<StockTakeListResponse>,
): Promise<StockTakeRound[]> => {
  const rounds: StockTakeRound[] = [];
  const seen = new Set<string>();
  let before: string | undefined;
  for (let page = 0; page < OPEN_ROUNDS_MAX_PAGES; page += 1) {
    const result = await list({ status: 'OPEN', limit: OPEN_ROUNDS_PAGE_SIZE, ...(before ? { before } : {}) });
    // A round served twice (a cursor that slipped) is still one round.
    result.rounds.forEach((round) => {
      if (!seen.has(round.docId)) {
        seen.add(round.docId);
        rounds.push(round);
      }
    });
    // A cursor that hands back the page just read would only read it again.
    if (!result.nextCursor || result.rounds.length === 0 || result.nextCursor === before) {
      break;
    }
    before = result.nextCursor;
  }
  return rounds;
};
